import { apiRequest, apiBlobUrl } from '../http';
import { ITurnoGeoRepository } from './ITurnoGeoRepository';
import {
  OvertimeCandidate,
  Overtime,
  OvertimeQuery,
  OvertimeReviewInput,
  Modality,
  CreateModalityInput,
  UpdateModalityInput,
  OfficeNetwork,
  CreateOfficeNetworkInput,
  UpdateOfficeNetworkInput,
  TurnoGeoParameter,
  TurnoGeoSettings,
  HomeLocation,
  RegisterHomeInput,
  UpdateHomeLocationInput,
  MyNetwork,
  NetworkReading,
  SaveReadingInput,
  ListReadingsFilters,
  UserProfile,
  UpsertUserProfileInput,
  UserSchedule,
  SetUserScheduleInput,
  BulkAssignScheduleInput,
  CreateRecordInput,
  TurnoGeoRecord,
  ListRecordsFilters,
  PagedResult,
  MyDay,
  CloseOpenShiftInput,
  AdminCloseShiftInput,
  CorrectRecordInput,
  RecordAuditEntry,
  OpenShift,
  ShiftClosureLog,
  ListShiftClosureFilters,
  ScheduleException,
  SaveScheduleExceptionInput,
  RespondPingInput,
  RespondPingResult,
  LocationPing,
  ListPingsFilters,
  Inconsistency,
  ListInconsistenciesFilters,
  ReviewInconsistencyInput,
  InconsistencySummary,
  Novelty,
  SaveNoveltyInput,
  ListNoveltiesFilters,
  ReviewNoveltyInput,
  ImportRowInput,
  ImportPreview,
  ImportResultRow,
  Employee,
  CredentialImportResult,
  Label,
  PublicLabel,
  SaveLabelInput,
  LocationContext,
  AdminSetLocationInput,
  EmployeePatch,
  Holiday,
  RateFactor,
  SaveRateFactorInput,
  HoursReport,
  HoursReportQuery,
  MyWeek,
  ImportMarkRow,
  ImportMarkResult,
} from './types';

export class RemoteTurnoGeoRepository implements ITurnoGeoRepository {
  constructor(private getToken: () => Promise<string | null>) {}

  async listModalities(includeInactive = false): Promise<Modality[]> {
    const token = await this.getToken();
    return apiRequest(`/api/turno-geo/modalities?includeInactive=${includeInactive}`, { token });
  }
  async createModality(input: CreateModalityInput): Promise<Modality> {
    const token = await this.getToken();
    return apiRequest('/api/turno-geo/modalities', { method: 'POST', body: input, token });
  }
  async updateModality(id: string, patch: UpdateModalityInput): Promise<Modality> {
    const token = await this.getToken();
    return apiRequest(`/api/turno-geo/modalities/${id}`, { method: 'PATCH', body: patch, token });
  }
  async deleteModality(id: string): Promise<void> {
    const token = await this.getToken();
    await apiRequest(`/api/turno-geo/modalities/${id}`, { method: 'DELETE', token });
  }

  async listOfficeNetworks(includeInactive = false): Promise<OfficeNetwork[]> {
    const token = await this.getToken();
    return apiRequest(`/api/turno-geo/office-networks?includeInactive=${includeInactive}`, { token });
  }
  async createOfficeNetwork(input: CreateOfficeNetworkInput): Promise<OfficeNetwork> {
    const token = await this.getToken();
    return apiRequest('/api/turno-geo/office-networks', { method: 'POST', body: input, token });
  }
  async updateOfficeNetwork(id: string, patch: UpdateOfficeNetworkInput): Promise<OfficeNetwork> {
    const token = await this.getToken();
    return apiRequest(`/api/turno-geo/office-networks/${id}`, { method: 'PATCH', body: patch, token });
  }
  async deleteOfficeNetwork(id: string): Promise<void> {
    const token = await this.getToken();
    await apiRequest(`/api/turno-geo/office-networks/${id}`, { method: 'DELETE', token });
  }

  async getConfiguration(): Promise<TurnoGeoSettings> {
    const token = await this.getToken();
    return apiRequest('/api/turno-geo/configuration', { token });
  }
  async updateConfiguration(settings: Partial<TurnoGeoSettings>): Promise<TurnoGeoSettings> {
    const token = await this.getToken();
    return apiRequest('/api/turno-geo/configuration', { method: 'PUT', body: settings, token });
  }
  async listParameters(): Promise<TurnoGeoParameter[]> {
    const token = await this.getToken();
    return apiRequest('/api/turno-geo/parameters', { token });
  }
  async updateParameter(code: string, value: string): Promise<TurnoGeoParameter> {
    const token = await this.getToken();
    return apiRequest(`/api/turno-geo/parameters/${encodeURIComponent(code)}`, { method: 'PUT', body: { value }, token });
  }

  async getMyNetwork(): Promise<MyNetwork> {
    const token = await this.getToken();
    return apiRequest('/api/turno-geo/my-network', { token });
  }

  async saveReading(input: SaveReadingInput): Promise<NetworkReading> {
    const token = await this.getToken();
    return apiRequest('/api/turno-geo/readings', { method: 'POST', body: input, token });
  }
  async listMyReadings(limit = 30): Promise<NetworkReading[]> {
    const token = await this.getToken();
    return apiRequest(`/api/turno-geo/readings/me?limit=${limit}`, { token });
  }
  async listReadings(filters: ListReadingsFilters): Promise<NetworkReading[]> {
    const token = await this.getToken();
    const qs = new URLSearchParams();
    if (filters.userId) qs.set('userId', filters.userId);
    if (filters.from) qs.set('from', filters.from);
    if (filters.to) qs.set('to', filters.to);
    if (filters.limit) qs.set('limit', String(filters.limit));
    return apiRequest(`/api/turno-geo/readings?${qs.toString()}`, { token });
  }

  async getMyLocations(): Promise<HomeLocation[]> {
    const token = await this.getToken();
    return apiRequest('/api/turno-geo/home-location/me', { token });
  }
  async registerMyHome(input: RegisterHomeInput): Promise<HomeLocation> {
    const token = await this.getToken();
    return apiRequest('/api/turno-geo/home-location/me', { method: 'POST', body: input, token });
  }
  async listHomeLocations(): Promise<HomeLocation[]> {
    const token = await this.getToken();
    return apiRequest('/api/turno-geo/home-locations', { token });
  }
  async reviewHomeLocation(id: string, status: 'APROBADA' | 'RECHAZADA'): Promise<HomeLocation> {
    const token = await this.getToken();
    return apiRequest(`/api/turno-geo/home-locations/${id}/review`, { method: 'PUT', body: { status }, token });
  }
  async updateHomeLocation(id: string, patch: UpdateHomeLocationInput): Promise<HomeLocation> {
    const token = await this.getToken();
    return apiRequest(`/api/turno-geo/home-locations/${id}`, { method: 'PATCH', body: patch, token });
  }
  async deleteHomeLocation(id: string): Promise<void> {
    const token = await this.getToken();
    await apiRequest(`/api/turno-geo/home-locations/${id}`, { method: 'DELETE', token });
  }

  async listUserProfiles(): Promise<UserProfile[]> {
    const token = await this.getToken();
    return apiRequest('/api/turno-geo/user-profiles', { token });
  }
  async upsertUserProfile(input: UpsertUserProfileInput): Promise<UserProfile> {
    const token = await this.getToken();
    return apiRequest('/api/turno-geo/user-profiles', { method: 'PUT', body: input, token });
  }
  async deleteUserProfile(id: string): Promise<void> {
    const token = await this.getToken();
    await apiRequest(`/api/turno-geo/user-profiles/${id}`, { method: 'DELETE', token });
  }

  async listUserSchedule(userId?: string): Promise<UserSchedule[]> {
    const token = await this.getToken();
    const params = userId ? `?userId=${userId}` : '';
    return apiRequest(`/api/turno-geo/user-schedule${params}`, { token });
  }
  async setUserSchedule(input: SetUserScheduleInput): Promise<UserSchedule> {
    const token = await this.getToken();
    return apiRequest('/api/turno-geo/user-schedule', { method: 'PUT', body: input, token });
  }
  async bulkAssignSchedule(input: BulkAssignScheduleInput): Promise<{ updated: number }> {
    const token = await this.getToken();
    return apiRequest('/api/turno-geo/user-schedule/bulk-assign', { method: 'POST', body: input, token });
  }
  async deleteUserScheduleDay(id: string): Promise<void> {
    const token = await this.getToken();
    await apiRequest(`/api/turno-geo/user-schedule/${id}`, { method: 'DELETE', token });
  }

  async createRecord(input: CreateRecordInput): Promise<TurnoGeoRecord> {
    const token = await this.getToken();
    return apiRequest('/api/turno-geo/records', { method: 'POST', body: input, token });
  }
  async listRecords(filters: ListRecordsFilters): Promise<PagedResult<TurnoGeoRecord>> {
    const token = await this.getToken();
    const params = new URLSearchParams();
    if (filters.userId) params.set('userId', filters.userId);
    if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
    if (filters.dateTo) params.set('dateTo', filters.dateTo);
    if (filters.recordType) params.set('recordType', filters.recordType);
    if (filters.modalityCode) params.set('modalityCode', filters.modalityCode);
    params.set('page', String(filters.page ?? 1));
    params.set('pageSize', String(filters.pageSize ?? 20));
    return apiRequest(`/api/turno-geo/records?${params.toString()}`, { token });
  }

  async revalidateRecords(filters: { userId?: string; dateFrom?: string; dateTo?: string }): Promise<{ reviewed: number; changed: number }> {
    const token = await this.getToken();
    return apiRequest('/api/turno-geo/records/revalidate', { method: 'POST', body: filters, token });
  }

  async getMyDay(): Promise<MyDay> {
    const token = await this.getToken();
    return apiRequest('/api/turno-geo/my-day', { token });
  }
  async closeOpenShift(input: CloseOpenShiftInput): Promise<TurnoGeoRecord> {
    const token = await this.getToken();
    return apiRequest('/api/turno-geo/records/close-open', { method: 'POST', body: input, token });
  }
  async adminCloseShift(input: AdminCloseShiftInput): Promise<TurnoGeoRecord> {
    const token = await this.getToken();
    return apiRequest('/api/turno-geo/records/admin-close', { method: 'POST', body: input, token });
  }
  async correctRecord(id: string, input: CorrectRecordInput): Promise<TurnoGeoRecord> {
    const token = await this.getToken();
    return apiRequest(`/api/turno-geo/records/${id}/correct`, { method: 'PUT', body: input, token });
  }
  async listRecordAudit(id: string): Promise<RecordAuditEntry[]> {
    const token = await this.getToken();
    return apiRequest(`/api/turno-geo/records/${id}/audit`, { token });
  }
  async listOpenShifts(): Promise<OpenShift[]> {
    const token = await this.getToken();
    return apiRequest('/api/turno-geo/open-shifts', { token });
  }
  async listShiftClosureLog(filters: ListShiftClosureFilters): Promise<ShiftClosureLog[]> {
    const token = await this.getToken();
    const params = new URLSearchParams();
    if (filters.userId) params.set('userId', filters.userId);
    if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
    if (filters.dateTo) params.set('dateTo', filters.dateTo);
    if (filters.eventType) params.set('eventType', filters.eventType);
    if (filters.limit) params.set('limit', String(filters.limit));
    return apiRequest(`/api/turno-geo/shift-closures?${params.toString()}`, { token });
  }

  async listScheduleExceptions(userId?: string): Promise<ScheduleException[]> {
    const token = await this.getToken();
    return apiRequest(`/api/turno-geo/schedule-exceptions${userId ? `?userId=${userId}` : ''}`, { token });
  }
  async createScheduleException(input: SaveScheduleExceptionInput): Promise<ScheduleException> {
    const token = await this.getToken();
    return apiRequest('/api/turno-geo/schedule-exceptions', { method: 'POST', body: input, token });
  }
  async updateScheduleException(id: string, input: SaveScheduleExceptionInput): Promise<ScheduleException> {
    const token = await this.getToken();
    return apiRequest(`/api/turno-geo/schedule-exceptions/${id}`, { method: 'PUT', body: input, token });
  }
  async respondPing(id: string, input: RespondPingInput): Promise<RespondPingResult> {
    const token = await this.getToken();
    return apiRequest(`/api/turno-geo/pings/${id}/respond`, { method: 'POST', body: input, token });
  }
  async listPings(filters: ListPingsFilters): Promise<PagedResult<LocationPing>> {
    const token = await this.getToken();
    const params = new URLSearchParams();
    if (filters.userId) params.set('userId', filters.userId);
    if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
    if (filters.dateTo) params.set('dateTo', filters.dateTo);
    if (filters.status) params.set('status', filters.status);
    params.set('page', String(filters.page ?? 1));
    params.set('pageSize', String(filters.pageSize ?? 20));
    return apiRequest(`/api/turno-geo/pings?${params.toString()}`, { token });
  }

  async listInconsistencies(filters: ListInconsistenciesFilters, scan = true): Promise<PagedResult<Inconsistency>> {
    const token = await this.getToken();
    const params = new URLSearchParams();
    for (const key of ['userId', 'code', 'status', 'severity', 'dateFrom', 'dateTo'] as const) {
      if (filters[key]) params.set(key, filters[key] as string);
    }
    params.set('page', String(filters.page ?? 1));
    params.set('pageSize', String(filters.pageSize ?? 20));
    params.set('scan', scan ? 'true' : 'false');
    return apiRequest(`/api/turno-geo/inconsistencies?${params.toString()}`, { token });
  }
  async inconsistencySummary(): Promise<InconsistencySummary> {
    const token = await this.getToken();
    return apiRequest('/api/turno-geo/inconsistencies/summary', { token });
  }
  async reviewInconsistency(id: string, input: ReviewInconsistencyInput): Promise<Inconsistency> {
    const token = await this.getToken();
    return apiRequest(`/api/turno-geo/inconsistencies/${id}/review`, { method: 'PUT', body: input, token });
  }

  async listNovelties(filters: ListNoveltiesFilters = {}): Promise<Novelty[]> {
    const token = await this.getToken();
    const params = new URLSearchParams();
    if (filters.userId) params.set('userId', filters.userId);
    if (filters.status) params.set('status', filters.status);
    if (filters.noveltyType) params.set('noveltyType', filters.noveltyType);
    return apiRequest(`/api/turno-geo/novelties?${params.toString()}`, { token });
  }
  async createNovelty(input: SaveNoveltyInput): Promise<Novelty> {
    const token = await this.getToken();
    return apiRequest('/api/turno-geo/novelties', { method: 'POST', body: input, token });
  }
  async reviewNovelty(id: string, input: ReviewNoveltyInput): Promise<Novelty> {
    const token = await this.getToken();
    return apiRequest(`/api/turno-geo/novelties/${id}/review`, { method: 'PUT', body: input, token });
  }
  async deleteNovelty(id: string): Promise<void> {
    const token = await this.getToken();
    await apiRequest(`/api/turno-geo/novelties/${id}`, { method: 'DELETE', token });
  }

  async listLabels(): Promise<PublicLabel[]> {
    const token = await this.getToken();
    return apiRequest('/api/turno-geo/labels', { token });
  }
  async listAllLabels(): Promise<Label[]> {
    const token = await this.getToken();
    return apiRequest('/api/turno-geo/labels/all', { token });
  }
  async createLabel(input: SaveLabelInput): Promise<Label> {
    const token = await this.getToken();
    return apiRequest('/api/turno-geo/labels', { method: 'POST', body: input, token });
  }
  async updateLabel(id: string, input: SaveLabelInput): Promise<Label> {
    const token = await this.getToken();
    return apiRequest(`/api/turno-geo/labels/${id}`, { method: 'PATCH', body: input, token });
  }
  async deleteLabel(id: string): Promise<void> {
    const token = await this.getToken();
    await apiRequest(`/api/turno-geo/labels/${id}`, { method: 'DELETE', token });
  }
  async getMyLocationContext(): Promise<LocationContext> {
    const token = await this.getToken();
    return apiRequest('/api/turno-geo/my-location-context', { token });
  }
  async adminSetLocation(input: AdminSetLocationInput): Promise<HomeLocation> {
    const token = await this.getToken();
    return apiRequest('/api/turno-geo/home-locations/for-user', { method: 'POST', body: input, token });
  }
  async updateEmployee(userId: string, patch: EmployeePatch): Promise<Employee> {
    const token = await this.getToken();
    return apiRequest(`/api/turno-geo/employees/${userId}`, { method: 'PUT', body: patch, token });
  }
  async listHolidays(year?: number): Promise<Holiday[]> {
    const token = await this.getToken();
    return apiRequest(`/api/turno-geo/holidays${year ? `?year=${year}` : ''}`, { token });
  }
  async createHoliday(input: { date: string; name: string }): Promise<Holiday> {
    const token = await this.getToken();
    return apiRequest('/api/turno-geo/holidays', { method: 'POST', body: input, token });
  }
  async updateHoliday(id: string, patch: { name?: string; isActive?: boolean }): Promise<Holiday> {
    const token = await this.getToken();
    return apiRequest(`/api/turno-geo/holidays/${id}`, { method: 'PATCH', body: patch, token });
  }
  async deleteHoliday(id: string): Promise<void> {
    const token = await this.getToken();
    await apiRequest(`/api/turno-geo/holidays/${id}`, { method: 'DELETE', token });
  }
  async generateHolidays(year: number): Promise<{ created: number; existing: number }> {
    const token = await this.getToken();
    return apiRequest('/api/turno-geo/holidays/generate', { method: 'POST', body: { year }, token });
  }
  async listRateFactors(): Promise<RateFactor[]> {
    const token = await this.getToken();
    return apiRequest('/api/turno-geo/rate-factors', { token });
  }
  async createRateFactor(input: SaveRateFactorInput): Promise<RateFactor> {
    const token = await this.getToken();
    return apiRequest('/api/turno-geo/rate-factors', { method: 'POST', body: input, token });
  }
  async updateRateFactor(id: string, input: SaveRateFactorInput): Promise<RateFactor> {
    const token = await this.getToken();
    return apiRequest(`/api/turno-geo/rate-factors/${id}`, { method: 'PATCH', body: input, token });
  }
  async deleteRateFactor(id: string): Promise<void> {
    const token = await this.getToken();
    await apiRequest(`/api/turno-geo/rate-factors/${id}`, { method: 'DELETE', token });
  }
  async getHoursReport(query: HoursReportQuery): Promise<HoursReport> {
    const token = await this.getToken();
    const qs = new URLSearchParams({ from: query.from, to: query.to });
    if (query.userId) qs.set('userId', query.userId);
    if (query.detail) qs.set('detail', '1');
    return apiRequest(`/api/turno-geo/hours-report?${qs.toString()}`, { token });
  }
  async getOvertimeCandidates(): Promise<OvertimeCandidate[]> {
    const token = await this.getToken();
    return apiRequest('/api/turno-geo/overtime/my-candidates', { token });
  }
  async requestOvertime(workDate: string, note: string): Promise<Overtime> {
    const token = await this.getToken();
    return apiRequest('/api/turno-geo/overtime/request', { method: 'POST', body: { workDate, note }, token });
  }
  async declineOvertime(workDate: string): Promise<Overtime> {
    const token = await this.getToken();
    return apiRequest('/api/turno-geo/overtime/decline', { method: 'POST', body: { workDate }, token });
  }
  async listMyOvertime(): Promise<Overtime[]> {
    const token = await this.getToken();
    return apiRequest('/api/turno-geo/overtime/mine', { token });
  }
  async listOvertime(query: OvertimeQuery = {}): Promise<Overtime[]> {
    const token = await this.getToken();
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) if (v) qs.set(k, v);
    return apiRequest(`/api/turno-geo/overtime?${qs.toString()}`, { token });
  }
  async reviewOvertime(id: string, input: OvertimeReviewInput): Promise<Overtime> {
    const token = await this.getToken();
    return apiRequest(`/api/turno-geo/overtime/${id}/review`, { method: 'PUT', body: input, token });
  }
  async sendOvertimeToHr(ids?: string[]): Promise<{ sent: number }> {
    const token = await this.getToken();
    return apiRequest('/api/turno-geo/overtime/send-to-hr', { method: 'POST', body: { ids }, token });
  }
  async getMyWeek(): Promise<MyWeek> {
    const token = await this.getToken();
    return apiRequest('/api/turno-geo/my-week', { token });
  }
  async importMarks(rows: ImportMarkRow[]): Promise<ImportMarkResult[]> {
    const token = await this.getToken();
    return apiRequest('/api/turno-geo/records/import', { method: 'POST', body: { rows }, token });
  }
  async getRecordPhotoUrl(recordId: string): Promise<string> {
    const token = await this.getToken();
    return apiBlobUrl(`/api/turno-geo/records/${recordId}/photo`, token);
  }
  async listCredentialUserIds(): Promise<string[]> {
    const token = await this.getToken();
    const r = await apiRequest<{ userIds: string[] }>('/api/turno-geo/credentials', { token });
    return r.userIds;
  }
  async getInitialPassword(userId: string): Promise<string | null> {
    const token = await this.getToken();
    const r = await apiRequest<{ password: string | null }>(`/api/turno-geo/credentials/${userId}`, { token });
    return r.password;
  }
  async setInitialPassword(userId: string, password: string): Promise<void> {
    const token = await this.getToken();
    await apiRequest(`/api/turno-geo/credentials/${userId}`, { method: 'PUT', body: { password }, token });
  }
  async importInitialPasswords(rows: { email: string; password: string }[]): Promise<CredentialImportResult[]> {
    const token = await this.getToken();
    return apiRequest('/api/turno-geo/credentials/import', { method: 'POST', body: { rows }, token });
  }
  async listEmployees(): Promise<Employee[]> {
    const token = await this.getToken();
    return apiRequest('/api/turno-geo/employees', { token });
  }
  async previewEmployeeImport(rows: ImportRowInput[]): Promise<ImportPreview> {
    const token = await this.getToken();
    return apiRequest('/api/turno-geo/employees/import/preview', { method: 'POST', body: { rows }, token });
  }
  async executeEmployeeImport(rows: ImportRowInput[], startIndex: number): Promise<ImportResultRow[]> {
    const token = await this.getToken();
    return apiRequest('/api/turno-geo/employees/import/execute', { method: 'POST', body: { rows, startIndex }, token });
  }

  async deleteScheduleException(id: string): Promise<void> {
    const token = await this.getToken();
    await apiRequest(`/api/turno-geo/schedule-exceptions/${id}`, { method: 'DELETE', token });
  }
}
